import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantInvitation.css";
import { API_BASE } from "../../../ApiUrl";
import PeopleTabs from "../Users/PeopleTabs";

const API_URL = `${API_BASE}/tenant-invitations/`;
const ROLES_API_URL = `${API_BASE}/roles/`;

const emptyInvitation = {
  email: "",
  role: ""
};

// Basic email sanity check — matches typical Django EmailField acceptance
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddTenantInvitation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editInvitation = location.state?.editInvitation || null;
  const isEditMode = Boolean(editInvitation?.id);

  const [invitations, setInvitations] = useState(() => {
    if (editInvitation) {
      return [
        {
          email: editInvitation.email || "",
          role: editInvitation.role ? String(editInvitation.role) : ""
        }
      ];
    }

    return [{ ...emptyInvitation }];
  });

  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // Load roles for the dropdown
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const response = await axios.get(ROLES_API_URL);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setRolesList(data);
      } catch (error) {
        console.error("Error fetching roles:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Roles",
          text: "Could not fetch the roles list. Please refresh the page."
        });
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setInvitations((prev) =>
      prev.map((inv, i) => (i === index ? { ...inv, [name]: value } : inv))
    );
  };

  const addInvitation = () => {
    if (isEditMode) return;
    setInvitations((prev) => [...prev, { ...emptyInvitation }]);
  };

  const removeInvitation = (index) => {
    if (isEditMode || invitations.length === 1) return;
    setInvitations((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRow = (inv, index) => {
    const label = `Invitation ${index + 1}`;

    if (!inv.email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide an Email for ${label}.`
      });
      return false;
    }

    if (!EMAIL_REGEX.test(inv.email.trim())) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: `Email for ${label} is not a valid email address.`
      });
      return false;
    }

    if (!inv.role) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select a Role for ${label}.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (inv, tenantUser) => ({
    email: inv.email.trim().toLowerCase(),
    role: Number(inv.role),
    tenant: tenantUser.tenant_id,
    user: tenantUser.user_id
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });
      return;
    }

    for (let i = 0; i < invitations.length; i++) {
      if (!validateRow(invitations[i], i)) return;
    }

    // Duplicate email check within the form
    const emails = invitations.map((inv) => inv.email.trim().toLowerCase());
    const duplicateIndex = emails.findIndex((e, i) => emails.indexOf(e) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Email",
        text: `Email "${invitations[
          duplicateIndex
        ].email.trim()}" is used more than once.`
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const payload = buildPayload(invitations[0], tenantUser);

        console.log("Update Tenant Invitation Payload:", payload);

        const response = await axios.patch(
          `${API_URL}${editInvitation.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Update Tenant Invitation Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "Invitation Updated Successfully",
          text: "Tenant invitation has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/invitations");
        return;
      }

      const payload = invitations.map((inv) => buildPayload(inv, tenantUser));

      console.log("Create Tenant Invitations Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Tenant Invitations Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "Invitations Sent Successfully",
        text: `${invitations.length} invitation${
          invitations.length > 1 ? "s" : ""
        } sent successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/invitations");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Tenant Invitation Error:"
          : "Create Tenant Invitations Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update tenant invitation."
        : "Unable to send tenant invitations.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Send Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/invitations");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-invite-page">
          <PeopleTabs/>
          <div className="tenant-invite-page-header">
            <div>
              <h2>
                {isEditMode
                  ? "Edit Invitation"
                  : "Create Invitations"}
              </h2>
              <p>
                {isEditMode
                  ? "Update the pending invitation details."
                  : "Invite one or multiple people to join the tenant."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tenant-invite-card">
              <div className="tenant-invite-form-body">
                {invitations.map((inv, index) => (
                  <div className="tenant-invite-record" key={index}>
                    <div className="tenant-invite-record-header">
                      <div>
                        <h4>
                          {isEditMode
                            ? "Invitation Details"
                            : `Invitation ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && invitations.length > 1 && (
                        <button
                          type="button"
                          className="tenant-invite-remove-btn"
                          onClick={() => removeInvitation(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="tenant-invite-form-grid">
                      <div className="tenant-invite-form-group">
                        <label>
                          Email <span>*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={inv.email}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="e.g. jane.doe@example.com"
                          maxLength={254}
                          autoComplete="off"
                          required
                        />
                        <p className="tenant-invite-hint">
                          The invitation link will be sent to this address.
                        </p>
                      </div>

                      <div className="tenant-invite-form-group">
                        <label>
                          Role <span>*</span>
                        </label>
                        <select
                          name="role"
                          value={inv.role}
                          onChange={(e) => handleChange(index, e)}
                          disabled={loadingRoles}
                          required
                        >
                          <option value="">
                            {loadingRoles
                              ? "Loading roles..."
                              : "Select role"}
                          </option>
                          {rolesList.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name ||
                                role.role_name ||
                                role.code ||
                                `Role ${role.id}`}
                            </option>
                          ))}
                        </select>
                        <p className="tenant-invite-hint">
                          Determines what the invited user can access.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="tenant-invite-add-btn"
                    onClick={addInvitation}
                  >
                    <span>+</span>
                    Add Another Invitation
                  </button>
                )}
              </div>
            </div>

            <div className="tenant-invite-form-actions">
              <button
                type="button"
                className="tenant-invite-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="tenant-invite-save-btn"
                disabled={loading || loadingRoles}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Sending..."
                  : isEditMode
                  ? "Update Invitation"
                  : `Send ${invitations.length} Invitation${
                      invitations.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTenantInvitation;